/**
 * OpenAI API Utility Functions
 * TypeScript utilities for interacting with OpenAI API
 */
import { TRACKED_CHANGES_GUIDANCE } from './aiGuidance';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const API_TOKEN = import.meta.env.VITE_ADDIN_API_TOKEN || '';

interface RiskAnalysisResult {
  risks: RiskItem[];
}

interface RiskItem {
  severity: 'high' | 'medium' | 'low';
  riskySentence: string;
  suggestion: string;
  revisedClause: string;
  category: string;
}

const MAX_TOKENS_PER_CHUNK = 6000; // Leave room for prompt and response
const CHUNK_OVERLAP = 500; // Overlap to avoid splitting sentences

/**
 * Estimates token count (rough approximation: 1 token ≈ 4 characters)
 */
function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/**
 * Splits text into chunks that fit within token limits
 */
function splitIntoChunks(text: string, maxTokens: number, overlap: number): string[] {
  const chunks: string[] = [];
  const estimatedTokens = estimateTokens(text);
  
  // If text fits in one chunk, return it
  if (estimatedTokens <= maxTokens) {
    return [text];
  }
  
  // Split by paragraphs first, then by sentences if needed
  const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0);
  let currentChunk = '';
  let currentTokens = 0;
  
  for (let i = 0; i < paragraphs.length; i++) {
    const paragraph = paragraphs[i];
    const paragraphTokens = estimateTokens(paragraph);
    
    // If adding this paragraph would exceed limit, save current chunk and start new one
    if (currentTokens + paragraphTokens > maxTokens && currentChunk.length > 0) {
      chunks.push(currentChunk.trim());
      
      // Start new chunk with overlap from previous chunk
      const overlapText = currentChunk.slice(-overlap);
      currentChunk = overlapText + '\n\n' + paragraph;
      currentTokens = estimateTokens(currentChunk);
    } else {
      currentChunk += (currentChunk ? '\n\n' : '') + paragraph;
      currentTokens += paragraphTokens;
    }
  }
  
  // Add the last chunk
  if (currentChunk.trim().length > 0) {
    chunks.push(currentChunk.trim());
  }
  
  return chunks;
}

function safeJsonParse(text: string): any | null {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function cleanAiJsonText(text: string): string {
  return (text || '').replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
}

function parseRisksFromContent(content: string): RiskItem[] | null {
  const cleanedContent = cleanAiJsonText(content);
  if (!cleanedContent) return null;

  let parsed: any = safeJsonParse(cleanedContent);
  if (typeof parsed === 'string') {
    parsed = safeJsonParse(parsed) ?? parsed;
  }

  if (Array.isArray(parsed)) return parsed as RiskItem[];
  if (parsed && typeof parsed === 'object') {
    if (Array.isArray((parsed as any).risks)) return (parsed as any).risks as RiskItem[];
  }

  const arrayMatch = cleanedContent.match(/\[[\s\S]*\]/);
  if (arrayMatch) {
    const repaired = arrayMatch[0].replace(/,\s*([}\]])/g, '$1');
    let arrayParsed: any = safeJsonParse(repaired);
    if (typeof arrayParsed === 'string') {
      arrayParsed = safeJsonParse(arrayParsed) ?? arrayParsed;
    }
    if (Array.isArray(arrayParsed)) return arrayParsed as RiskItem[];
  }

  const objMatch = cleanedContent.match(/\{[\s\S]*\}/);
  if (objMatch) {
    const repaired = objMatch[0].replace(/,\s*([}\]])/g, '$1');
    let objParsed: any = safeJsonParse(repaired);
    if (typeof objParsed === 'string') {
      objParsed = safeJsonParse(objParsed) ?? objParsed;
    }
    if (objParsed && typeof objParsed === 'object' && Array.isArray((objParsed as any).risks)) {
      return (objParsed as any).risks as RiskItem[];
    }
  }

  return null;
}

/**
 * Analyzes a single chunk of contract text for risks
 */
async function analyzeChunk(chunkText: string, chunkNumber: number, totalChunks: number, tenantId: string): Promise<RiskItem[]> {
  const prompt = `You are a legal contract risk analysis expert. Analyze the following contract text (chunk ${chunkNumber} of ${totalChunks}) and identify ALL potential risks, including but not limited to:

- Unclear or ambiguous language
- Missing liability limitations
- Unfair termination clauses
- Intellectual property issues
- Payment terms risks
- Confidentiality concerns
- Indemnification problems
- Force majeure gaps
- Dispute resolution issues
- Any clauses that could be problematic for either party

For EACH risk you identify, provide:
1. Severity level: "high", "medium", or "low"
2. riskySentence: The exact sentence or phrase from the text that contains the risk
3. suggestion: A clear explanation of why this is risky and what should be improved
4. revisedClause: An improved version of the risky clause
5. category: One of: Legal, Financial, Operational, IP, Confidentiality, Liability, Termination, or Other

IMPORTANT: You MUST find at least some risks. Even if the contract seems well-written, identify areas that could be improved or clarified. Return a JSON array with at least 2-3 risks if possible.

Return ONLY a valid JSON array in this exact format (no markdown, no code blocks, just the JSON):
[
  {
    "severity": "high",
    "riskySentence": "exact sentence from the contract text",
    "suggestion": "explanation of the risk",
    "revisedClause": "improved version",
    "category": "Legal"
  }
]

Contract text to analyze:
${chunkText}

Return the JSON array now:`;

  const attempts: Array<{ temperature: number; systemMessage: string }> = [
    {
      temperature: 0.3,
      systemMessage:
        'You are a legal contract risk analysis expert. Analyze contracts and identify risks with specific suggestions and revised clauses. Always return valid JSON arrays. You MUST find risks - even well-written contracts have areas for improvement.' +
        TRACKED_CHANGES_GUIDANCE,
    },
    {
      temperature: 0,
      systemMessage:
        'Return ONLY a valid JSON array of risk objects. No markdown, no code fences, no extra text. Include severity (high|medium|low), riskySentence, suggestion, revisedClause, category. You MUST find risks.' +
        TRACKED_CHANGES_GUIDANCE,
    },
  ];

  let risks: RiskItem[] = [];
  let lastContent: string = '';

  for (let attemptIndex = 0; attemptIndex < attempts.length; attemptIndex++) {
    const attempt = attempts[attemptIndex];

    // 90 s timeout per chunk attempt — Claude Opus can take 30-60 s on a
    // long prompt; without this, a hung connection looks like an instant
    // "Failed to fetch" with no useful detail.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);

    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/api/addin/ai-analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-addin-token': API_TOKEN,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          prompt,
          systemMessage: attempt.systemMessage,
          temperature: attempt.temperature,
          maxTokens: 3000,
          tenantId,
        }),
        signal: controller.signal,
      });
    } catch (err: any) {
      clearTimeout(timeoutId);
      const isAbort = err?.name === 'AbortError';
      throw new Error(
        isAbort
          ? `AI analysis timed out after 90s. Try again — if it persists, the AI provider may be rate-limited.`
          : `Network error reaching AI service: ${err?.message || err}`,
      );
    }
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detail = errorData?.error || (await response.text().catch(() => '')) || 'Unknown error';
      throw new Error(`AI API ${response.status}: ${String(detail).slice(0, 300)}`);
    }

    const data = await response.json();
    const content = data.content || '';
    lastContent = content;

    console.log(`Chunk ${chunkNumber} raw response:`, content.substring(0, 500));

    const parsedRisks = parseRisksFromContent(content);

    if (!parsedRisks || parsedRisks.length === 0) {
      continue;
    }

    risks = parsedRisks;
    break;
  }

  if (!Array.isArray(risks)) {
    console.warn(`Chunk ${chunkNumber} returned non-array result:`, risks);
    return [];
  }

  console.log(`Chunk ${chunkNumber} parsed risks:`, risks.length);

  risks = risks.filter((risk) => {
    const isValid = 
      risk &&
      typeof risk === 'object' &&
      risk.severity &&
      risk.riskySentence &&
      risk.suggestion &&
      risk.revisedClause &&
      risk.category;
    
    if (!isValid) {
      console.warn(`Invalid risk object in chunk ${chunkNumber}:`, risk);
    }
    
    return isValid;
  });

  console.log(`Chunk ${chunkNumber} valid risks:`, risks.length);
  if (risks.length === 0 && lastContent) {
    console.warn(`Chunk ${chunkNumber} returned no valid risks after parsing/validation`);
  }
  return risks;
}

/**
 * Analyzes contract text for risks using OpenAI
 * Handles long documents by splitting into chunks
 * @param documentText - The text content of the document to analyze
 * @param tenantId - Tenant ID for fetching AI keys from CLM
 * @param onProgress - Optional callback for progress updates
 * @returns Promise with risk analysis results
 */
export async function analyzeContractRisks(
  documentText: string,
  tenantId: string,
  onProgress?: (current: number, total: number) => void
): Promise<RiskAnalysisResult> {
  try {
    // Split document into chunks if needed
    const chunks = splitIntoChunks(documentText, MAX_TOKENS_PER_CHUNK, CHUNK_OVERLAP);
    
    if (chunks.length === 0) {
      return { risks: [] };
    }

    // Analyze each chunk
    const allRisks: RiskItem[] = [];
    let successfulChunks = 0;
    let lastError: unknown = null;
    
    for (let i = 0; i < chunks.length; i++) {
      if (onProgress) {
        onProgress(i + 1, chunks.length);
      }
      
      try {
        const chunkRisks = await analyzeChunk(chunks[i], i + 1, chunks.length, tenantId);
        allRisks.push(...chunkRisks);
        successfulChunks++;
        
        // Small delay between chunks to avoid rate limiting
        if (i < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (error) {
        console.error(`Error analyzing chunk ${i + 1}:`, error);
        lastError = error;
        // Continue with other chunks even if one fails
        if (onProgress) {
          onProgress(i + 1, chunks.length);
        }
      }
    }

    if (successfulChunks === 0 && lastError) {
      throw lastError;
    }

    // Remove duplicate risks (same risky sentence)
    const uniqueRisks = allRisks.filter((risk, index, self) =>
      index === self.findIndex((r) => r.riskySentence === risk.riskySentence)
    );

    return { risks: uniqueRisks };
  } catch (error) {
    console.error('Error analyzing contract risks:', error);
    throw error;
  }
}

export type { RiskAnalysisResult, RiskItem };
